import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { MapPin, Phone, Mail, Clock, Award, Users, Shield, Star } from 'lucide-react';
import useSEO from '@/hooks/useSEO';

const AboutPage = () => {
  useSEO('about', 'About Us | We Have Your Car');
  const [content, setContent] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchContent();
    fetchSettings();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/page-content/about`);
      setContent(response.data);
    } catch (error) {
      console.error('Error fetching about content:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/site-settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a1628] flex items-center justify-center">
        <div className="animate-pulse text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a1628]" data-testid="about-page">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#132238] to-[#1a4d8c]/30 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4 font-['Exo_2']">
            About <span className="gradient-text">We Have Your Car</span>
          </h1>
          <p className="text-xl text-[#a8b8d0] max-w-2xl">
            Your trusted Austin car dealership specializing in quality used vehicles with flexible financing options.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Story Section */}
          <div className="lg:col-span-2">
            <div className="bg-[#132238] rounded-xl p-8 border border-[#1a2d47]">
              <h2 className="text-2xl font-bold text-white mb-6">
                {content?.title || 'Our Story'}
              </h2>
              <div className="text-[#a8b8d0] space-y-4 whitespace-pre-line">
                {content?.content || `Welcome to We Have Your Car, your trusted Austin car dealership. We specialize in quality used vehicles with flexible financing options for all credit types.

Our mission is to help you find the perfect vehicle at a price you can afford. With years of experience in the automotive industry, we pride ourselves on providing exceptional customer service and a no-pressure buying experience.

Visit us today at our location in Austin, TX, and let us help you drive away in your dream car!`}
              </div>
            </div>

            {/* Why Choose Us */}
            <div className="grid sm:grid-cols-2 gap-6 mt-8">
              <div className="bg-[#132238] rounded-xl p-6 border border-[#1a2d47]">
                <div className="p-3 bg-[#ff6b35]/20 rounded-lg w-fit mb-4">
                  <Award className="text-[#ff6b35]" size={24} />
                </div>
                <h3 className="text-white font-semibold mb-2">Quality Vehicles</h3>
                <p className="text-[#a8b8d0] text-sm">
                  Every vehicle in our inventory is carefully inspected to ensure quality and reliability.
                </p>
              </div>
              
              <div className="bg-[#132238] rounded-xl p-6 border border-[#1a2d47]">
                <div className="p-3 bg-[#22c55e]/20 rounded-lg w-fit mb-4">
                  <Users className="text-[#22c55e]" size={24} />
                </div>
                <h3 className="text-white font-semibold mb-2">Customer First</h3>
                <p className="text-[#a8b8d0] text-sm">
                  Our team is dedicated to providing a stress-free, no-pressure car buying experience.
                </p>
              </div>
              
              <div className="bg-[#132238] rounded-xl p-6 border border-[#1a2d47]">
                <div className="p-3 bg-[#1a4d8c]/40 rounded-lg w-fit mb-4">
                  <Shield className="text-[#1a4d8c]" size={24} />
                </div>
                <h3 className="text-white font-semibold mb-2">Financing Options</h3>
                <p className="text-[#a8b8d0] text-sm">
                  We work with all credit types to find financing solutions that work for you.
                </p>
              </div>
              
              <div className="bg-[#132238] rounded-xl p-6 border border-[#1a2d47]">
                <div className="p-3 bg-[#ffc107]/20 rounded-lg w-fit mb-4">
                  <Star className="text-[#ffc107]" size={24} />
                </div>
                <h3 className="text-white font-semibold mb-2">Great Reviews</h3>
                <p className="text-[#a8b8d0] text-sm">
                  Our customers love us! Check out our 5-star reviews on Google.
                </p>
              </div>
            </div>
          </div>

          {/* Contact Sidebar */}
          <div>
            <div className="bg-[#132238] rounded-xl p-6 border border-[#1a2d47] sticky top-24">
              <h3 className="text-xl font-bold text-white mb-6">Visit Us Today</h3>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <MapPin className="text-[#ff6b35] mt-1" size={20} />
                  <div>
                    <p className="text-white font-medium">Address</p>
                    <p className="text-[#a8b8d0] text-sm">
                      {settings?.address || '8203 Bluff Springs Rd, Unit 4'}<br />
                      {settings?.city || 'Austin'}, {settings?.state || 'TX'} {settings?.zip || '78744'}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Phone className="text-[#ff6b35] mt-1" size={20} />
                  <div>
                    <p className="text-white font-medium">Phone</p>
                    <a href={`tel:${settings?.phone?.replace(/\D/g, '') || '5126660016'}`} 
                       className="text-[#a8b8d0] text-sm hover:text-[#ff6b35]">
                      {settings?.phone || '(512) 666-0016'}
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Mail className="text-[#ff6b35] mt-1" size={20} />
                  <div>
                    <p className="text-white font-medium">Email</p>
                    <a href={`mailto:${settings?.email || 'info@wehaveyourcar.com'}`} 
                       className="text-[#a8b8d0] text-sm hover:text-[#ff6b35]">
                      {settings?.email || 'info@wehaveyourcar.com'}
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Clock className="text-[#ff6b35] mt-1" size={20} />
                  <div>
                    <p className="text-white font-medium">Business Hours</p>
                    <p className="text-[#a8b8d0] text-sm">
                      {settings?.business_hours || 'Mon-Sat: 10AM - 6PM'}<br />
                      Sunday: Closed
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-6 space-y-3">
                <Link to="/inventory" className="btn-accent w-full text-center block">
                  Browse Inventory
                </Link>
                <Link to="/contact-us" className="btn-primary w-full text-center block">
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
